import { useEffect, useMemo, useState } from 'preact/hooks';
import { HOSTED_URL } from '../data/content';
import './Status.scss';

type Result =
	| { kind: 'loading' }
	| { kind: 'up'; latencyMs: number; checkedAt: string }
	| { kind: 'down'; reason: string; checkedAt: string };

/**
 * Minimal status page. Polls the backend's `/api/v1/healthz` once a
 * minute and renders up/down. Foundation for a real status page later
 * (Atlassian Statuspage, BetterUptime) — when we move there, this
 * route can simply iframe their public status URL or be replaced
 * outright.
 *
 * Why a page on the marketing site rather than a separate sub-domain
 * service: dev-tools users expect `/status` to live where the rest of
 * the marketing surface lives. When uptime needs a real SLO dashboard,
 * we'll spin up `status.pageloop.dev`.
 */
export function Status() {
	const [state, setState] = useState<Result>({ kind: 'loading' });
	const healthUrl = useMemo(() => `${HOSTED_URL}/api/v1/healthz`, []);

	useEffect(() => {
		let cancelled = false;
		let timer: ReturnType<typeof setTimeout> | null = null;

		async function check() {
			const start = performance.now();
			try {
				const res = await fetch(healthUrl, {
					// Bypass the SW + browser cache so a stale 200 doesn't paper
					// over a real outage.
					cache: 'no-store',
					// Hard cap — if the backend takes >5s, treat as down.
					signal: AbortSignal.timeout(5_000),
				});
				if (cancelled) return;
				if (!res.ok) {
					setState({
						kind: 'down',
						reason: `Health probe returned HTTP ${res.status}`,
						checkedAt: new Date().toISOString(),
					});
				} else {
					setState({
						kind: 'up',
						latencyMs: Math.round(performance.now() - start),
						checkedAt: new Date().toISOString(),
					});
				}
			} catch (err) {
				if (cancelled) return;
				setState({
					kind: 'down',
					reason: err instanceof Error ? err.message : 'Network error',
					checkedAt: new Date().toISOString(),
				});
			}
			if (!cancelled) timer = setTimeout(check, 60_000);
		}

		void check();
		return () => {
			cancelled = true;
			if (timer) clearTimeout(timer);
		};
	}, [healthUrl]);

	return (
		<section class="m-status" id="status">
			<div class="m-status__inner">
				<header class="m-status__head">
					<span class="m-status__eyebrow">status</span>
					<h1 class="m-status__title">
						{state.kind === 'up' && 'all systems normal.'}
						{state.kind === 'down' && 'service disruption.'}
						{state.kind === 'loading' && 'checking…'}
					</h1>
					<p class="m-status__sub">
						Live readout of the hosted PageLoop API at{' '}
						<code>{HOSTED_URL.replace(/^https?:\/\//, '')}</code>.
					</p>
				</header>

				<div class={`m-status__card m-status__card--${state.kind}`}>
					<div class="m-status__dot" aria-hidden="true" />
					<div class="m-status__body">
						<div class="m-status__label">
							{state.kind === 'up' && 'API · operational'}
							{state.kind === 'down' && 'API · unavailable'}
							{state.kind === 'loading' && 'API · checking'}
						</div>
						{state.kind === 'up' && (
							<div class="m-status__meta">
								Last check {formatTime(state.checkedAt)} · {state.latencyMs} ms
							</div>
						)}
						{state.kind === 'down' && (
							<div class="m-status__meta">
								{state.reason} · last check {formatTime(state.checkedAt)}
							</div>
						)}
						{state.kind === 'loading' && (
							<div class="m-status__meta">Probing {healthUrl}</div>
						)}
					</div>
				</div>

				<p class="m-status__foot">
					For real-time incident detail and SLA breakdowns, our dedicated status page is
					on the roadmap. Until then, this widget probes the API every 60 seconds.
				</p>
			</div>
		</section>
	);
}

function formatTime(iso: string): string {
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return iso;
	return d.toLocaleTimeString(undefined, {
		hour: '2-digit',
		minute: '2-digit',
		second: '2-digit',
	});
}
